import React, { useState } from 'react';
import { PolicyViewer, PolicyType } from './PolicyViewer';

interface PolicyAcceptanceProps {
  onAccept: (accepted: boolean) => void;
  language?: 'tr' | 'en';
  showError?: boolean;
}

interface PolicyInfo {
  type: PolicyType;
  title: { tr: string; en: string };
  description: { tr: string; en: string };
  icon: string;
  required: boolean;
}

const PolicyIcon = ({ type }: { type: PolicyType }) => {
  const iconClass = "w-6 h-6";

  switch (type) {
    case 'terms_of_use':
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    case 'privacy_policy':
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      );
    case 'cookie_policy':
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'gdpr_compliance':
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.618 5.984A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016zM12 9v2m0 4h.01" />
        </svg>
      );
    case 'antifraud_policy':
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      );
    default:
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
  }
};

const policies: PolicyInfo[] = [
  {
    type: 'terms_of_use',
    title: { tr: 'Kullanım Şartları', en: 'Terms of Use' },
    description: {
      tr: 'Platform kullanım koşulları ve yükümlülükler',
      en: 'Platform usage conditions and obligations'
    },
    icon: 'gavel',
    required: true,
  },
  {
    type: 'privacy_policy',
    title: { tr: 'Gizlilik Politikası', en: 'Privacy Policy' },
    description: {
      tr: 'Kişisel verilerinizin nasıl toplandığı ve işlendiği',
      en: 'How your personal data is collected and processed'
    },
    icon: 'security',
    required: true,
  },
  {
    type: 'cookie_policy',
    title: { tr: 'Çerez Politikası', en: 'Cookie Policy' },
    description: {
      tr: 'Çerezlerin nasıl kullanıldığı ve yönetildiği',
      en: 'How cookies are used and managed'
    },
    icon: 'cookie',
    required: true,
  },
  {
    type: 'gdpr_compliance',
    title: { tr: 'GDPR Uyum Politikası', en: 'GDPR Compliance Policy' },
    description: {
      tr: 'Avrupa veri koruma düzenlemelerine uyum',
      en: 'Compliance with European data protection regulations'
    },
    icon: 'shield',
    required: true,
  },
  {
    type: 'antifraud_policy',
    title: { tr: 'Anti-Fraud Politikası', en: 'Anti-Fraud Policy' },
    description: {
      tr: 'Sahtecilik ve kötüye kullanıma karşı önlemler',
      en: 'Measures against fraud and abuse'
    },
    icon: 'description',
    required: true,
  },
];

export const PolicyAcceptance: React.FC<PolicyAcceptanceProps> = ({
  onAccept,
  language = 'tr',
  showError = false,
}) => {
  const [acceptedPolicies, setAcceptedPolicies] = useState<Set<PolicyType>>(new Set());
  const [allAccepted, setAllAccepted] = useState(false);
  const [viewingPolicy, setViewingPolicy] = useState<PolicyType | null>(null);

  const handlePolicyToggle = (policyType: PolicyType) => {
    const newAccepted = new Set(acceptedPolicies);
    if (newAccepted.has(policyType)) {
      newAccepted.delete(policyType);
    } else {
      newAccepted.add(policyType);
    }
    setAcceptedPolicies(newAccepted);

    // Check if all required policies are accepted
    const allRequired = policies.filter(p => p.required).every(p => newAccepted.has(p.type));
    setAllAccepted(allRequired);
    onAccept(allRequired);
  };

  const handleAcceptAll = () => {
    if (allAccepted) {
      setAcceptedPolicies(new Set());
      setAllAccepted(false);
      onAccept(false);
    } else {
      const allPolicyTypes = policies.map(p => p.type);
      setAcceptedPolicies(new Set(allPolicyTypes));
      setAllAccepted(true);
      onAccept(true);
    }
  };

  return (
    <>
      <div className="my-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-900">
          {language === 'tr' ? 'Politika ve Sözleşmeler' : 'Policies and Agreements'}
        </h2>

        {showError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {language === 'tr'
              ? 'Devam etmek için tüm zorunlu politikaları kabul etmelisiniz.'
              : 'You must accept all required policies to continue.'}
          </div>
        )}

        <div className="bg-white rounded-lg border border-gray-200 p-2">
          {policies.map((policy) => (
            <div
              key={policy.type}
              className={`mb-2 p-4 rounded-lg transition-all duration-300 ${
                acceptedPolicies.has(policy.type)
                  ? 'bg-green-50 border border-green-200'
                  : 'bg-gray-50 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 mt-1">
                  {acceptedPolicies.has(policy.type) ? (
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ) : (
                    <div className="text-gray-400">
                      <PolicyIcon type={policy.type} />
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-medium text-gray-900">
                      {policy.title[language]}
                    </h3>
                    {policy.required && (
                      <span className="text-sm text-red-600 font-medium">
                        *{language === 'tr' ? 'Zorunlu' : 'Required'}
                      </span>
                    )}
                  </div>

                  <p className="text-sm text-gray-600 mb-2">
                    {policy.description[language]}
                  </p>

                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setViewingPolicy(policy.type);
                    }}
                    className="text-sm text-indigo-600 hover:text-indigo-500 underline"
                  >
                    {language === 'tr' ? 'Politikayı Oku' : 'Read Policy'}
                  </button>
                </div>

                <div className="flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={acceptedPolicies.has(policy.type)}
                    onChange={() => handlePolicyToggle(policy.type)}
                    className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    aria-label={`Accept ${policy.title[language]}`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={allAccepted}
              onChange={handleAcceptAll}
              className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 mr-3"
            />
            <span className="text-base font-medium text-gray-900">
              {language === 'tr'
                ? 'Tüm politikaları okudum ve kabul ediyorum'
                : 'I have read and accept all policies'}
            </span>
          </label>
        </div>

        <p className="text-sm text-gray-500 mt-4">
          {language === 'tr'
            ? 'Bu politikalar düzenli olarak güncellenir. Değişikliklerden haberdar olmak için düzenli olarak kontrol etmenizi öneririz.'
            : 'These policies are regularly updated. We recommend checking them regularly to stay informed of changes.'}
        </p>
      </div>

      {/* Policy Viewer Dialog */}
      {viewingPolicy && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setViewingPolicy(null)}></div>
          <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] mx-4">
            <div className="p-6">
              <PolicyViewer
                policyType={viewingPolicy}
                language={language}
                onClose={() => setViewingPolicy(null)}
              />
            </div>
            <div className="border-t px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => setViewingPolicy(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                {language === 'tr' ? 'Kapat' : 'Close'}
              </button>
              <button
                onClick={() => {
                  handlePolicyToggle(viewingPolicy);
                  setViewingPolicy(null);
                }}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                  acceptedPolicies.has(viewingPolicy)
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-indigo-600 hover:bg-indigo-700'
                }`}
              >
                {acceptedPolicies.has(viewingPolicy) ? (
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
                {acceptedPolicies.has(viewingPolicy)
                  ? (language === 'tr' ? 'Kabul Edildi' : 'Accepted')
                  : (language === 'tr' ? 'Kabul Et' : 'Accept')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};